import { U21Divider } from 'app/shared/u21-ui/components';
import { SavedFilterBar } from 'app/modules/filters/components/SavedFilterBar';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Filter,
  FilterContext,
  FilterOption,
  SavedFilter,
} from 'app/modules/filters/models';
import { useSelector } from 'react-redux';
import { selectAgent } from 'app/modules/session/selectors';
import {
  areFiltersEqual,
  getValidFilters,
  resolveSavedFilters,
} from 'app/modules/filters/utils';

interface Props {
  appliedSavedFilter: SavedFilter | undefined;
  defaultFilters: Filter[];
  filters: Filter[];
  filterContext: FilterContext;
  matchingSavedFilter: SavedFilter | undefined;
  onChange: (filters: Filter[]) => void;
  options: FilterOption[];
  savedFilters: SavedFilter[];
  setAppliedSavedFilter: Dispatch<SetStateAction<SavedFilter | undefined>>;
}

export const SavedFilters = ({
  appliedSavedFilter,
  defaultFilters,
  filters,
  filterContext,
  matchingSavedFilter,
  onChange,
  options,
  savedFilters,
  setAppliedSavedFilter,
}: Props) => {
  // pending applied filter held until parent's filters catch up — avoids
  // flashing dirty state in the gap between dispatch and parent re-render
  const pendingApplySavedFilterRef = useRef<SavedFilter>();

  const agent = useSelector(selectAgent);

  // passively lock onto a saved filter when the current filters match one and
  // nothing is applied — e.g. filters loaded from the URL or built by hand to
  // coincide with a saved set. An in-flight click is owned by the pending ref
  // below (it preserves *which* filter was selected when bodies are identical),
  // so skip auto-lock while a pending apply is settling.
  const appliedSavedFilterRef = useRef(appliedSavedFilter);
  useEffect(() => {
    appliedSavedFilterRef.current = appliedSavedFilter;
  }, [appliedSavedFilter]);
  useEffect(() => {
    if (
      !appliedSavedFilterRef.current &&
      !pendingApplySavedFilterRef.current &&
      matchingSavedFilter
    ) {
      setAppliedSavedFilter(matchingSavedFilter);
    }
  }, [matchingSavedFilter, setAppliedSavedFilter]);

  // when viewing a non-owned saved filter, clear applied saved filter when
  // current filters do not match
  useEffect(() => {
    const applied = appliedSavedFilterRef.current;
    if (
      applied &&
      applied.created_by_id !== agent.id &&
      !areFiltersEqual(
        resolveSavedFilters(applied.filters, options, defaultFilters),
        filters,
      )
    ) {
      setAppliedSavedFilter(undefined);
    }
  }, [filters, agent.id, options, defaultFilters, setAppliedSavedFilter]);

  // drop applied state when clearing filters back to default
  useEffect(() => {
    if (
      appliedSavedFilterRef.current &&
      areFiltersEqual(filters, defaultFilters)
    ) {
      setAppliedSavedFilter(undefined);
    }
  }, [filters, defaultFilters, setAppliedSavedFilter]);

  const staleSavedFilterIds = useMemo(
    () =>
      new Set(
        savedFilters
          .filter(
            (i) =>
              getValidFilters(i.filters, options).length < i.filters.length,
          )
          .map((i) => i.id),
      ),
    [options, savedFilters],
  );

  const onApplySavedFilter = useCallback(
    (savedFilter: SavedFilter) => {
      onChange(
        resolveSavedFilters(savedFilter.filters, options, defaultFilters),
      );
      // drop any currently-applied filter immediately so it can't flash dirty
      // while `filters` catches up; the pending ref re-locks the new one once
      // the parent's filters reflect what we just dispatched
      setAppliedSavedFilter(undefined);
      pendingApplySavedFilterRef.current = savedFilter;
    },
    [onChange, options, defaultFilters, setAppliedSavedFilter],
  );

  // commit applied state once parent's filters reflect what we set —
  // avoids dirty flash between dispatch and parent re-render
  useEffect(() => {
    const pending = pendingApplySavedFilterRef.current;
    if (!pending) {
      return;
    }
    if (
      areFiltersEqual(
        resolveSavedFilters(pending.filters, options, defaultFilters),
        filters,
      )
    ) {
      pendingApplySavedFilterRef.current = undefined;
      setAppliedSavedFilter(pending);
    }
  }, [filters, options, defaultFilters, setAppliedSavedFilter]);

  const isDirty = useCallback(
    (savedFilter: SavedFilter) =>
      !areFiltersEqual(
        resolveSavedFilters(savedFilter.filters, options, defaultFilters),
        filters,
      ),
    [filters, options, defaultFilters],
  );

  if (!savedFilters.length) {
    return null;
  }

  return (
    <div>
      <SavedFilterBar
        appliedSavedFilter={appliedSavedFilter}
        defaultFilters={defaultFilters}
        filterContext={filterContext}
        filters={filters}
        isDirty={isDirty}
        onApplySavedFilter={onApplySavedFilter}
        onAppliedSavedFilterChange={setAppliedSavedFilter}
        onChange={onChange}
        savedFilters={savedFilters}
        staleSavedFilterIds={staleSavedFilterIds}
      />
      <U21Divider horizontal />
    </div>
  );
};
